import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormsModule } from '@angular/forms';
import { CompanyConfigService } from '../../services/company-config.service';
import { CompanyConfig } from '../../models/company-config.model';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { ModuleAccessService, CompanyModuleItem } from '../../services/module-access.service';

@Component({
  selector: 'app-company-config',
  imports: [CommonModule, ReactiveFormsModule, FormsModule, InputTextModule, CardModule, ButtonModule],
  template: `
    <div class="px-8 py-4 max-h-screen overflow-y-auto">
      <p-card class="m-2">
        <form [formGroup]="form" (ngSubmit)="save()" class="space-y-6">
          
          <div class="flex justify-between items-center border-b pb-4">
            <h2 class="text-xl font-bold text-gray-800">Configuração da Empresa</h2>
          </div>

          <!-- Mensagem de Status -->
          <div *ngIf="message" 
               class="p-4 rounded-md"
               [ngClass]="messageType === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'">
            {{ message }}
          </div>

          <!-- Informações Básicas -->
          <div class="space-y-4">
            <h3 class="text-lg font-semibold text-gray-700 border-b pb-2">Informações Básicas</h3>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Razão Social -->
              <div>
                <label class="block mb-1 text-sm font-medium text-gray-700">Razão Social <span class="text-red-500">*</span></label>
                <input 
                  pInputText
                  type="text" 
                  formControlName="razaoSocial"
                  placeholder="Digite a razão social"
                  class="w-full"
                  [ngClass]="{'ng-invalid ng-dirty': isFieldInvalid('razaoSocial')}">
                <small class="text-red-500" *ngIf="isFieldInvalid('razaoSocial')">Razão social é obrigatória</small>
              </div>

              <!-- Nome Fantasia -->
              <div>
                <label class="block mb-1 text-sm font-medium text-gray-700">Nome Fantasia <span class="text-red-500">*</span></label>
                <input 
                  pInputText
                  type="text" 
                  formControlName="nomeFantasia"
                  placeholder="Digite o nome fantasia"
                  class="w-full"
                  [ngClass]="{'ng-invalid ng-dirty': isFieldInvalid('nomeFantasia')}">
                <small class="text-red-500" *ngIf="isFieldInvalid('nomeFantasia')">Nome fantasia é obrigatório</small>
              </div>

              <!-- CNPJ -->
              <div>
                <label class="block mb-1 text-sm font-medium text-gray-700">CNPJ <span class="text-red-500">*</span></label>
                <input 
                  pInputText
                  type="text" 
                  formControlName="cnpj"
                  (input)="formatCnpj($event)"
                  placeholder="00.000.000/0000-00"
                  maxlength="18"
                  class="w-full"
                  [ngClass]="{'ng-invalid ng-dirty': isFieldInvalid('cnpj')}">
                <small class="text-red-500" *ngIf="isFieldInvalid('cnpj')">CNPJ é obrigatório e deve estar no formato 00.000.000/0000-00</small>
              </div>

              <!-- Inscrição Estadual -->
              <div>
                <label class="block mb-1 text-sm font-medium text-gray-700">Inscrição Estadual</label>
                <input 
                  pInputText
                  type="text" 
                  formControlName="inscricaoEstadual"
                  placeholder="Digite a inscrição estadual"
                  class="w-full">
              </div>
            </div>
          </div>

          <!-- Contato -->
          <div class="space-y-4">
            <h3 class="text-lg font-semibold text-gray-700 border-b pb-2">Contato</h3>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Email -->
              <div>
                <label class="block mb-1 text-sm font-medium text-gray-700">Email <span class="text-red-500">*</span></label>
                <input 
                  pInputText
                  type="email" 
                  formControlName="email"
                  placeholder="contato@empresa.com"
                  class="w-full"
                  [ngClass]="{'ng-invalid ng-dirty': isFieldInvalid('email')}">
                <small class="text-red-500" *ngIf="isFieldInvalid('email')">Email é obrigatório e deve ser válido</small>
              </div>

              <!-- Telefone -->
              <div>
                <label class="block mb-1 text-sm font-medium text-gray-700">Telefone <span class="text-red-500">*</span></label>
                <input 
                  pInputText
                  type="tel" 
                  formControlName="telefone"
                  (input)="formatTelefone($event)"
                  placeholder="(00) 0000-0000"
                  maxlength="15"
                  class="w-full"
                  [ngClass]="{'ng-invalid ng-dirty': isFieldInvalid('telefone')}">
                <small class="text-red-500" *ngIf="isFieldInvalid('telefone')">Telefone é obrigatório</small>
              </div>
            </div>
          </div>

          <!-- Endereço -->
          <div class="space-y-4">
            <h3 class="text-lg font-semibold text-gray-700 border-b pb-2">Endereço</h3>
            
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <!-- CEP -->
              <div>
                <label class="block mb-1 text-sm font-medium text-gray-700">CEP <span class="text-red-500">*</span></label>
                <input 
                  pInputText
                  type="text" 
                  formControlName="cep"
                  (input)="formatCep($event)"
                  placeholder="00000-000"
                  maxlength="9"
                  class="w-full"
                  [ngClass]="{'ng-invalid ng-dirty': isFieldInvalid('cep')}">
                <small class="text-red-500" *ngIf="isFieldInvalid('cep')">CEP é obrigatório</small>
              </div>

              <!-- Estado -->
              <div>
                <label class="block mb-1 text-sm font-medium text-gray-700">Estado <span class="text-red-500">*</span></label>
                <input 
                  pInputText
                  type="text" 
                  formControlName="estado"
                  placeholder="Digite o estado"
                  class="w-full"
                  [ngClass]="{'ng-invalid ng-dirty': isFieldInvalid('estado')}">
                <small class="text-red-500" *ngIf="isFieldInvalid('estado')">Estado é obrigatório</small>
              </div>

              <!-- Cidade -->
              <div>
                <label class="block mb-1 text-sm font-medium text-gray-700">Cidade <span class="text-red-500">*</span></label>
                <input 
                  pInputText
                  type="text" 
                  formControlName="cidade"
                  placeholder="Digite a cidade"
                  class="w-full"
                  [ngClass]="{'ng-invalid ng-dirty': isFieldInvalid('cidade')}">
                <small class="text-red-500" *ngIf="isFieldInvalid('cidade')">Cidade é obrigatória</small>
              </div>

              <!-- Logradouro -->
              <div class="md:col-span-2">
                <label class="block mb-1 text-sm font-medium text-gray-700">Logradouro <span class="text-red-500">*</span></label>
                <input 
                  pInputText
                  type="text" 
                  formControlName="logradouro"
                  placeholder="Digite o logradouro"
                  class="w-full"
                  [ngClass]="{'ng-invalid ng-dirty': isFieldInvalid('logradouro')}">
                <small class="text-red-500" *ngIf="isFieldInvalid('logradouro')">Logradouro é obrigatório</small>
              </div>

              <!-- Número -->
              <div>
                <label class="block mb-1 text-sm font-medium text-gray-700">Número <span class="text-red-500">*</span></label>
                <input 
                  pInputText
                  type="text" 
                  formControlName="numero"
                  placeholder="Digite o número"
                  class="w-full"
                  [ngClass]="{'ng-invalid ng-dirty': isFieldInvalid('numero')}">
                <small class="text-red-500" *ngIf="isFieldInvalid('numero')">Número é obrigatório</small>
              </div>

              <!-- Complemento -->
              <div>
                <label class="block mb-1 text-sm font-medium text-gray-700">Complemento</label>
                <input 
                  pInputText
                  type="text" 
                  formControlName="complemento"
                  placeholder="Digite o complemento"
                  class="w-full">
              </div>

              <!-- Bairro -->
              <div>
                <label class="block mb-1 text-sm font-medium text-gray-700">Bairro <span class="text-red-500">*</span></label>
                <input 
                  pInputText
                  type="text" 
                  formControlName="bairro"
                  placeholder="Digite o bairro"
                  class="w-full"
                  [ngClass]="{'ng-invalid ng-dirty': isFieldInvalid('bairro')}">
                <small class="text-red-500" *ngIf="isFieldInvalid('bairro')">Bairro é obrigatório</small>
              </div>
            </div>
          </div>

          <!-- Módulos habilitados da empresa -->
          <div class="space-y-4">
            <h3 class="text-lg font-semibold text-gray-700 border-b pb-2">Módulos da Empresa</h3>
            <p class="text-sm text-gray-500">
              Marque os módulos que estarão disponíveis no menu para esta empresa.
            </p>

            <div *ngIf="isLoadingModules" class="text-sm text-gray-500">Carregando módulos...</div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                type="button"
                *ngFor="let module of companyModules"
                (click)="toggleModuleCard(module)"
                class="w-full text-left border-2 rounded-md px-5 py-4 flex items-center justify-between transition-colors"
                [ngClass]="module.enabled
                  ? 'border-orange-500 bg-orange-50 text-orange-800'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'"
              >
                <span class="font-medium text-base">{{ module.name }}</span>
                <span
                  class="w-6 h-6 border-[3px] rounded-sm flex items-center justify-center"
                  [ngClass]="module.enabled ? 'border-orange-600' : 'border-gray-400'"
                >
                  <i *ngIf="module.enabled" class="pi pi-check text-xs text-orange-600"></i>
                </span>
              </button>
            </div>

            <div class="flex justify-end">
              <p-button
                label="Salvar Módulos"
                icon="pi pi-save"
                severity="secondary"
                (onClick)="saveCompanyModules()"
                [disabled]="isLoadingModules || companyModules.length === 0"
                [loading]="isSavingModules">
              </p-button>
            </div>
          </div>


          <!-- Botões de Ação -->
          <div class="flex justify-end space-x-4 pt-6 border-t">
            <p-button 
              label="Cancelar"
              icon="pi pi-times"
              severity="secondary"
              (onClick)="loadCompanyConfig()"
              [disabled]="isSaving">
            </p-button>
            <p-button 
              label="Salvar Configuração"
              icon="pi pi-save"
              severity="primary"
              type="submit"
              [loading]="isSaving"
              [disabled]="isLoading">
            </p-button>
          </div>
        </form>
      </p-card>
    </div>
  `
})
export class CompanyConfigComponent implements OnInit {
  
  form!: FormGroup;
  isLoading = false;
  isSaving = false;
  isLoadingModules = false;
  isSavingModules = false;
  message = '';
  messageType: 'success' | 'error' = 'success';
  companyModules: CompanyModuleItem[] = [];
  

  constructor(
    private companyConfigService: CompanyConfigService,
    private fb: FormBuilder,
    private moduleAccessService: ModuleAccessService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadCompanyConfig();
    this.loadCompanyModules();
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      razaoSocial: ['', [Validators.required]],
      nomeFantasia: ['', [Validators.required]],
      inscricaoEstadual: [''],
      telefone: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      cidade: ['', [Validators.required]],
      estado: ['', [Validators.required]],
      cnpj: ['', [Validators.required, this.cnpjValidator]],
      cep: ['', [Validators.required]],
      logradouro: ['', [Validators.required]],
      numero: ['', [Validators.required]],
      complemento: [''],
      bairro: ['', [Validators.required]]
    });
  }

  loadCompanyConfig(): void {
    this.isLoading = true;
    this.companyConfigService.get().subscribe({
      next: (response) => {
        if (response.data) {
          this.form.patchValue(response.data);
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar configuração da empresa:', error);
        this.showMessage('Erro ao carregar configuração da empresa', 'error');
        this.isLoading = false;
      }
    });
  }


  save(): void {
    if (this.form.valid) {
      this.isSaving = true;
      const formData = this.form.value;
      
      const operation = formData.id 
        ? this.companyConfigService.update(formData.id, formData)
        : this.companyConfigService.save(formData);

      operation.subscribe({
        next: (response) => {
          this.form.patchValue(response.data);
          this.showMessage('Configuração salva com sucesso!', 'success');
        },
        error: (error) => {
          console.error('Erro ao salvar configuração:', error);
          this.showMessage('Erro ao salvar configuração da empresa', 'error');
          this.isSaving = false;
        }
      });
    } else {
      this.markFormGroupTouched();
      this.showMessage('Por favor, preencha todos os campos obrigatórios', 'error');
    }
  }


  private cnpjValidator(control: any) {
    const cnpj = control.value;
    if (!cnpj) return null;
    
    const cnpjRegex = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/;
    return cnpjRegex.test(cnpj) ? null : { invalidCnpj: true };
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  private markFormGroupTouched(): void {
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
    });
  }

  private showMessage(message: string, type: 'success' | 'error'): void {
    this.message = message;
    this.messageType = type;
    setTimeout(() => {
      this.message = '';
    }, 5000);
  }

  loadCompanyModules(): void {
    this.isLoadingModules = true;
    const companyId = this.moduleAccessService.getLoggedCompanyId();

    this.moduleAccessService.getCompanyModules(companyId).subscribe({
      next: (response) => {
        this.companyModules = response.data ?? [];
        this.isLoadingModules = false;
      },
      error: () => {
        this.showMessage('Erro ao carregar módulos da empresa', 'error');
        this.isLoadingModules = false;
      }
    });
  }

  toggleModuleCard(module: CompanyModuleItem): void {
    module.enabled = !module.enabled;
  }

  saveCompanyModules(): void {
    const companyId = this.moduleAccessService.getLoggedCompanyId();
    const modulesPayload = this.companyModules.map((module) => ({
      moduleId: module.moduleId,
      enabled: module.enabled
    }));

    this.isSavingModules = true;
    this.moduleAccessService.updateCompanyModules(companyId, modulesPayload).subscribe({
      next: ({ data }) => {
        this.companyModules = data ?? [];
        this.moduleAccessService.setEnabledModules(
          this.companyModules.filter((x) => x.active && x.enabled).map((x) => x.code)
        );
        this.showMessage('Módulos da empresa atualizados com sucesso!', 'success');
        this.isSavingModules = false;
      },
      error: () => {
        this.showMessage('Erro ao salvar módulos da empresa', 'error');
        this.isSavingModules = false;
      }
    });
  }

  formatCnpj(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length <= 14) {
      value = value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
      this.form.patchValue({ cnpj: value });
    }
  }

  formatCep(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length <= 8) {
      value = value.replace(/^(\d{5})(\d{3})$/, '$1-$2');
      this.form.patchValue({ cep: value });
    }
  }

  formatTelefone(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length <= 11) {
      if (value.length <= 10) {
        value = value.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
      } else {
        value = value.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
      }
      this.form.patchValue({ telefone: value });
    }
  }

}
